import moment, { Moment } from 'moment';
import { map, get, isEmpty, findKey } from 'lodash';
import dayjs, { Dayjs } from 'dayjs';
import duration from 'dayjs/plugin/duration';

import { DEFAULT_TIMEZONE_OFFSET } from 'config';

dayjs.extend(duration);

// Type definitions
export interface Timezone {
  code: string;
  name: string;
  offset: number;
  tz?: string;
}

export interface Country {
  countryID: number;
  code: string;
  name: string;
  currency: string;
  timezones: Timezone[];
  cities: any[];
  languages: any[];
}

const COUNTRY_IDS_OVERRIDE: Record<number, string> = { 5: 'Vietnam Time', 10: 'Cambodia Time' };

export const DEFAULT_DATE_FORMAT = 'DD/MM/YYYY';
export const DEFAULT_DATETIME_FORMAT = 'DD/MM/YYYY hh:mm a';
export const DEFAULT_TIME_FORMAT = 'hh:mm a';
export const RFC3339_TIME_FORMAT = 'YYYY-MM-DDTHH:mm:ssZ';
export const UNIX_SECONDS_FORMAT = 'X';

export const toFormattedDate = (t: number | string, format: string = DEFAULT_DATETIME_FORMAT): string => {
  const stringDate = moment(t).format(format);
  return stringDate === 'Invalid date' ? '' : stringDate;
};

export const toMoment = (t?: number | string, format: string = DEFAULT_DATE_FORMAT): Moment | string => {
  return t ? moment(t, format) : '';
};

export const extractTimezone = (countryHash: Record<string, Country>, countryKey: number | string, tzCode: string = ''): number => {
  // Consider countryKey as countryCode, if it fails then consider it as countryID
  const country = !isEmpty(countryHash) &&
    (
      get(countryHash, findKey(countryHash, ['code', countryKey])) ||
      get(countryHash, `${countryKey}`)
    );

  // If country is found then return it's UTC offset, else return default
  return (country && getUTCOffsetForCountry(country, tzCode)) || DEFAULT_TIMEZONE_OFFSET;
};

export const getUTCOffsetForCountry = (country: Country, preferredTzCode: string = ''): number => {
  const timezones = get(country, 'timezones', []);
  let preferedTz = getDefaultTimezoneForCountry(country);
  for (const tz of timezones) {
    if (get(tz, 'code') === preferredTzCode) {
      preferedTz = tz;
      break;
    }
  }

  return get(preferedTz, 'offset', DEFAULT_TIMEZONE_OFFSET);
};

export const getDefaultTimezoneForCountry = (country: Country): Timezone | undefined => {
  const timezones = get(country, 'timezones', []);
  // If there is multiple timezones for a country, this select the most middle one in the list.
  return timezones[Math.floor(timezones.length / 2)];
};

export const getTimezoneOffset = (tz: Timezone): number => {
  return get(tz, 'offset', DEFAULT_TIMEZONE_OFFSET);
};

export const formatTimezoneOffset = (tzOffset: number): string => {
  const h = Math.floor(Math.abs(tzOffset));
  const m = Math.round((Math.abs(tzOffset) - h) * 60);

  let prefix = '+';
  const formatted = `${(`0${h}`).slice(-2)}:${(`0${m}`).slice(-2)}`;
  if (tzOffset < 0) {
    prefix = '-';
  }
  return prefix + formatted;
};

export const getTimezoneOptions = (timezones: Timezone[]): { text: string; value: string }[] => {
  return map(timezones || [], (tz) => ({
    text: `${get(tz, 'code')} (UTC ${formatTimezoneOffset(getTimezoneOffset(tz))})`,
    value: get(tz, 'code'),
  }));
};

export const getTimezoneOptionsWithOffset = (timezones: Timezone[]): { text: string; value: { code: string; offset: number } }[] => {
  return map(timezones || [], (tz) => ({
    text: `${get(tz, 'code')} (UTC ${formatTimezoneOffset(getTimezoneOffset(tz))})`,
    value: {
      code: get(tz, 'code'),
      offset: get(tz, 'offset'),
    },
  }));
};

export const formatTimezoneString = (countryID: number, timezone: Timezone): string => {
  return `${COUNTRY_IDS_OVERRIDE[countryID] || timezone.name} (UTC + ${dayjs
    .duration({
      hours: parseInt(String(timezone.offset), 10),
      minutes: (timezone.offset % 1) * 60,
    })
    .format('HH:mm')})`;
};

export const convertTimeWindowToHoursInUTC = (timeWindow: Dayjs[], timezones: Timezone[]): number[] => {
  if (timeWindow && timeWindow[0] && timeWindow[1]) {
    const hoursInUTC: number[] = [];
    const offset = timezones && timezones[0] ? timezones[0].offset : 0;
    for (let localHour = timeWindow[0].hour(); localHour <= timeWindow[1].hour(); localHour += 1) {
      // Convert local time to UTC: 
      // UTC hour = local hour + local minute/60 - offset
      const localMinute = timeWindow[0].minute();
      const utcHour = (localHour + localMinute / 60 - offset + 24) % 24;
      hoursInUTC.push(utcHour);
    }
    return hoursInUTC;
  }
  return [];
};

/**
 * Converts a UTC hour to local time based on timezone offset.
 * Handles fractional offsets (e.g., UTC+5:30, UTC+6:30).
 * E.g. UTC hour 16 with offset 6.5 -> Dayjs object with hour 22 and minute 30
 */
export const convertUTCHourToLocalTime = (utcHour: number, timezoneOffset: number): Dayjs => {
  const baseDate = dayjs().startOf('day');
  const offsetHours = Math.floor(timezoneOffset);
  const offsetMinutes = Math.round((timezoneOffset % 1) * 60);

  let localHour = utcHour + offsetHours;
  const localMinute = offsetMinutes;

  // Handle hour overflow
  if (localHour >= 24) {
    localHour -= 24;
  } else if (localHour < 0) {
    localHour += 24;
  }

  return baseDate.hour(localHour).minute(localMinute).second(0).millisecond(0);
};

/**
 * Converts an array of UTC hours to an array of time windows in local time.
 * Converts to local time first, then splits consecutive local hours into segments.
 * E.g. [1,2,3,13,14] with offset 8 -> Dayjs objects [[9:00, 11:00], [21:00, 22:00]]
 * E.g. [20,21,22,23,0] with offset 8 -> Dayjs objects [[4:00, 8:00]]
 * E.g. [15,16,17] with offset 6.5 -> Dayjs objects [[21:30, 23:30]]
 */
export const convertHoursInUTCToTimeWindow = (hoursInUTC: number[], timezoneOffset: number): Dayjs[][] => {
  if (!hoursInUTC || hoursInUTC.length === 0) {
    return [];
  }

  // Convert UTC hours to local time first, then sort by local hour
  const localTimes = hoursInUTC.map((utcHour) => {
    const localDayjs = convertUTCHourToLocalTime(utcHour, timezoneOffset);
    return { localHour: localDayjs.hour(), dayjs: localDayjs };
  });
  localTimes.sort((a, b) => a.localHour - b.localHour);

  // Split into consecutive segments based on local hours
  const segments: Dayjs[][] = [];
  let currentSegment = [localTimes[0]];

  for (let i = 1; i < localTimes.length; i += 1) {
    if (localTimes[i].localHour === currentSegment[currentSegment.length - 1].localHour + 1) {
      currentSegment.push(localTimes[i]);
    } else {
      segments.push([currentSegment[0].dayjs, currentSegment[currentSegment.length - 1].dayjs]);
      currentSegment = [localTimes[i]];
    }
  }
  segments.push([currentSegment[0].dayjs, currentSegment[currentSegment.length - 1].dayjs]);

  return segments;
};

/**
 * Formats an array of UTC hours into a readable string.
 * Splits consecutive UTC hours into segments and converts to local time.
 * E.g. [1,2,3,13,14] with offset 8 -> "09:00 to 12:00, 21:00 to 23:00"
 * E.g. [15,16,17] with offset 6.5 -> "21:30 to 23:30"
 */
export const convertHoursInUTCToDisplayTime = (hoursInUTC: number[], timezoneOffset: number): string => {
  const timeWindowSegments = convertHoursInUTCToTimeWindow(hoursInUTC, timezoneOffset);
  // Format each segment as h:mm A to h:mm A, e.g. 10:00 AM to 12:00 PM
  const formattedSegments = timeWindowSegments.map((segment) => {
    const startStr = segment[0].format('HH:mm');
    const endStr = segment[1].format('HH:mm');
    return `${startStr} to ${endStr}`;
  });
  return formattedSegments.join(', ');
};

